export interface DagNode {
	id: string;
	dependsOn: string[];
}

export interface Position {
	x: number;
	y: number;
}

const HORIZONTAL_GAP = 280;
const VERTICAL_GAP = 120;

export function layoutDag(nodes: DagNode[]): Map<string, Position> {
	const positions = new Map<string, Position>();
	if (nodes.length === 0) return positions;

	// Assign layers via topological depth
	const layers = new Map<string, number>();
	const nodeMap = new Map(nodes.map((n) => [n.id, n]));

	function getLayer(id: string): number {
		const cached = layers.get(id);
		if (cached !== undefined) return cached;
		const node = nodeMap.get(id);
		if (!node || node.dependsOn.length === 0) {
			layers.set(id, 0);
			return 0;
		}
		const maxParentLayer = Math.max(...node.dependsOn.map((dep) => getLayer(dep)));
		const layer = maxParentLayer + 1;
		layers.set(id, layer);
		return layer;
	}

	for (const node of nodes) {
		getLayer(node.id);
	}

	// Group nodes by layer
	const layerGroups = new Map<number, string[]>();
	for (const node of nodes) {
		const layer = layers.get(node.id) ?? 0;
		if (!layerGroups.has(layer)) layerGroups.set(layer, []);
		layerGroups.get(layer)?.push(node.id);
	}

	// Assign positions
	for (const [layer, ids] of layerGroups) {
		const x = layer * HORIZONTAL_GAP;
		const totalHeight = (ids.length - 1) * VERTICAL_GAP;
		const startY = -totalHeight / 2;
		for (let i = 0; i < ids.length; i++) {
			positions.set(ids[i], { x, y: startY + i * VERTICAL_GAP });
		}
	}

	return positions;
}
